// AppRoutes.tsx
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { BusinessTypes } from '../utils/businessUtils';
import ProtectedRoute from './ProtectedRoute';
import { useAuth } from '../contexts/AuthContext';

// Імпортуйте ваші компоненти сторінок
import SalonHomePage from '../pages/SalonHomePage';
import HomePage from '../pages/HomePage';
import WelcomePage from '../pages/WelcomePage';
import PublicHomePage from '../pages/PublicHomePage/PublicHomePage';
import LoginPage from '../pages/LoginPage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import PublicOfferPage from '../pages/PublicOfferPage';
import ProfilePage from '../pages/ProfilePage';
import BusinessSettings from '../pages/SettingsPage/BusinessSettings/BusinessSettings';
import CustomizationSettings from '../pages/SettingsPage/CustomizationSettings/CustomizationSettings';
import CategoriesSettings from '../pages/SettingsPage/CategoriesSettings';
import ScheduleSettings from '../pages/SettingsPage/ScheduleSettings';
import PackageManagement from '../pages/SettingsPage/PackageManagement';
import CurrencySettings from '../pages/SettingsPage/CurrencySettings';
import LocationTest from '../components/test/LocationTest';
import { AppointmentsPage, ClientsPage, ServicesPage, StaffPage, WorkdaysPage, SitePage, VisitHistoryPage, FavoriteMastersPage, ReviewsPage, SupportPage, MyRecordsPage } from '../pages';
import SubscriptionPage from '../pages/SubscriptionPage/SubscriptionPage';
import AdminBusinessesPage from '../pages/AdminBusinessesPage/AdminBusinessesPage';
import AdminClientsPage from '../pages/AdminClientsPage/AdminClientsPage';
import AdminSubscriptionsPage from '../pages/AdminSubscriptionsPage';
import AdminSupportPage from '../pages/AdminSupportPage/AdminSupportPage';
import AdminCitiesPage from '../pages/AdminCitiesPage/AdminCitiesPage';
import AdminClearPage from '../pages/AdminClearPage/AdminClearPage';
import PaymentSuccessPage from '../pages/PaymentSuccessPage/PaymentSuccessPage';
import { PUBLIC_ROUTES, PRIVATE_ROUTES } from '../constants/routes';

// Компонент для динамічного вибору сторінки
const BusinessHomePage: React.FC = () => {
  const { user } = useAuth();
  const businessType = (user as any)?.businessType as BusinessTypes | undefined;
  
  console.log('BusinessHomePage - user:', user);
  console.log('BusinessHomePage - businessType:', businessType);
  
  switch (businessType) {
    case BusinessTypes.SALON:
      console.log('BusinessHomePage - Returning SalonHomePage');
      return <SalonHomePage />;
    default:
      console.log('BusinessHomePage - businessType not SALON, returning SalonHomePage for masters');
      // Для майстрів без businessType все одно повертаємо SalonHomePage
      if (user?.role === 'master' || user?.role === 'owner' || user?.role === 'super_admin') {
        console.log('BusinessHomePage - User is master/owner, returning SalonHomePage');
        return <SalonHomePage />;
      }
      return <HomePage />;
  }
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  // Determine default route based on user role
  const getDefaultRoute = () => {
    // console.log('AppRoutes - user:', user);
    // console.log('AppRoutes - user role:', user?.role);
    
    if (user?.role === 'super_admin') {
      //console.log('AppRoutes - Redirecting to ADMIN_SUPPORT');
      return PRIVATE_ROUTES.ADMIN_SUPPORT;
    }
    if (user?.role === 'master' || user?.role === 'owner') {
      //console.log('AppRoutes - Redirecting to HOME (/home)');
      return PRIVATE_ROUTES.HOME;
    }
    if (user?.role === 'client') {
      //console.log('AppRoutes - Redirecting to HOME (/home)');
      return PRIVATE_ROUTES.HOME;
    }
    //console.log('AppRoutes - Redirecting to HOME');
    return PRIVATE_ROUTES.HOME;
  };

  return (
    <Routes>
      {/* 🌐 Публічна головна сторінка зі списком салонів */}
      <Route
        path={PUBLIC_ROUTES.HOME}
        element={
          isAuthenticated ? (
            <Navigate to={getDefaultRoute()} replace />
          ) : (
            <PublicHomePage />
          )
        }
      />

      {/* 🔐 Публічний маршрут для логіну */}
      <Route 
        path={PUBLIC_ROUTES.LOGIN} 
        element={<LoginPage />} 
      />

      {/* 📋 Публічний маршрут для Політики конфіденційності */}
      <Route 
        path={PUBLIC_ROUTES.PRIVACY_POLICY} 
        element={<PrivacyPolicyPage />} 
      />

      {/* 📄 Публічний маршрут для Публічної оферти */}
      <Route 
        path={PUBLIC_ROUTES.PUBLIC_OFFER} 
        element={<PublicOfferPage />} 
      />

      {/* 💳 Публічний маршрут для сторінки успіху платежу */}
      <Route 
        path={PUBLIC_ROUTES.PAYMENT_SUCCESS} 
        element={<PaymentSuccessPage />} 
      />
      {/* Explicit dashboard route so navigation to PRIVATE_ROUTES.DASHBOARD is matched */}
      <Route
        path={PRIVATE_ROUTES.DASHBOARD}
        element={
          <ProtectedRoute>
            <BusinessHomePage />
          </ProtectedRoute>
        }
      />
      
      {/* Home route for all users - welcome page */}
      <Route
        path={PRIVATE_ROUTES.HOME}
        element={
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        }
      />

      {/* Profile page */}
      <Route
        path={PRIVATE_ROUTES.PROFILE}
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Main navigation pages */}
      <Route
        path={PRIVATE_ROUTES.APPOINTMENTS}
        element={
          <ProtectedRoute>
            <AppointmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.CLIENTS}
        element={
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SERVICES}
        element={
          <ProtectedRoute>
            <ServicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.STAFF}
        element={
          <ProtectedRoute>
            <StaffPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SETTINGS_WORKDAYS}
        element={
          <ProtectedRoute>
            <WorkdaysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SETTINGS_SITE}
        element={
          <ProtectedRoute>
            <SitePage />
          </ProtectedRoute>
        }
      />

      {/* Settings pages */}
      <Route
        path={PRIVATE_ROUTES.SETTINGS_BUSINESS}
        element={
          <ProtectedRoute>
            <BusinessSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SETTINGS_CUSTOMIZATION}
        element={
          <ProtectedRoute>
            <CustomizationSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SETTINGS_CATEGORIES}
        element={
          <ProtectedRoute>
            <CategoriesSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SETTINGS_SCHEDULE}
        element={
          <ProtectedRoute>
            <ScheduleSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SETTINGS_PACKAGES}
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <PackageManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SETTINGS_CURRENCY}
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <CurrencySettings />
          </ProtectedRoute>
        }
      />

      {/* Location Test Route */}
      <Route path="/test/location" element={<LocationTest />} />

      {/* Subscription Page Route */}
      <Route
        path={PRIVATE_ROUTES.SUBSCRIPTION}
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />

      {/* Client pages */}
      <Route
        path={PRIVATE_ROUTES.MY_RECORDS}
        element={
          <ProtectedRoute>
            <MyRecordsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.VISIT_HISTORY}
        element={
          <ProtectedRoute>
            <VisitHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.FAVORITE_MASTERS}
        element={
          <ProtectedRoute>
            <FavoriteMastersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.REVIEWS}
        element={
          <ProtectedRoute>
            <ReviewsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.SUPPORT}
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />

      {/* Admin pages */}
      <Route
        path={PRIVATE_ROUTES.ADMIN_BUSINESSES}
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <AdminBusinessesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.ADMIN_CLIENTS}
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <AdminClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.ADMIN_SUBSCRIPTIONS}
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <AdminSubscriptionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.ADMIN_SUPPORT}
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <AdminSupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.ADMIN_CITIES}
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <AdminCitiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={PRIVATE_ROUTES.ADMIN_CLEAR}
        element={
          <ProtectedRoute requiredRole={['super_admin']}>
            <AdminClearPage />
          </ProtectedRoute>
        }
      />

      {/* Catch-all: redirect unknown routes to home */}
      <Route path="*" element={<Navigate to={PUBLIC_ROUTES.HOME} replace />} />
      
      {/* Інші маршрути... */}
    </Routes>
  );
};

export default AppRoutes;
